import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';

function CustomSearchableDropdown({
  items = [],
  value,
  labelKey,
  valueKey,
  placeholder = "Select",
  searchPlaceholder = "Search",
  noOptionsMessage = "",
  onChange,
  searchable = true,
  disabled = false,
  error = false,
  rootClassName = ""
}) {
  const { t } = useTranslation("common");
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [touched, setTouched] = useState(false); // track if user changed value
  const dropdownRef = useRef(null);

  // store initial value only once
  const initialValueRef = useRef(value);

  const selectedItem = items.find(
    (item) => String(item[valueKey]) === String(value)
  ) || null;

  // reset touched if items change
  useEffect(() => {
    initialValueRef.current = value; // keep track of new initial value
    setTouched(false); // reset touched
  }, [items]);

  // filtered items for search
  const filteredItems = useMemo(() => {
    if (!searchable) return items;
    return items.filter((item) =>
      item[labelKey]
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [items, searchText, searchable, labelKey]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    // ✅ For single-option dropdown, always mark as touched on click
    const isSingleOption = items.length === 1;

    if (isSingleOption || String(item[valueKey]) !== String(initialValueRef.current)) {
      setTouched(true);
    }

    onChange(item[valueKey]);
    setIsOpen(false);
    setSearchText("");
  };


  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setSearchText("");
    }
  };

  return (
    <div className={`${rootClassName} ctm-dropdown-inner d-block position-relative`.trim()} ref={dropdownRef}>
      {/* Trigger */}
      <a
        className={`ctm-dropdown 
          ${isOpen ? "dropdown-open" : ""} 
          ${disabled ? "ctm-dropdown-disabled" : ""} 
          ${touched ? "ctm-success" : ""}   // ✅ green border & font only if touched
          ${error ? "error-form" : ""}`      // ❌ red border if error/ Required
        } 
          
        onClick={toggleDropdown}
      >
        {selectedItem ? selectedItem[labelKey] : placeholder}
      </a>

      {isOpen && !disabled && (
        <div className="ctm-dropdown-panel">
          {searchable && (
            <div className="px-3 pt-3 pb-2">
              <input
                type="text"
                className="form-control"
                placeholder={t("search") }
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <ul className="ctm-dropdown-menu ctm-dropdown-search">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <li
                  key={item[valueKey]}
                  onClick={() => handleSelect(item)}
                  className={value === item[valueKey] ? "ctm-selected-option" : ""}
                >
                  <a>{item[labelKey]}</a>
                </li>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                {noOptionsMessage || t("react_no_options_found")}
              </div>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CustomSearchableDropdown;
